/**
 * Travaux et Administratif — deux listes, deux tempéraments.
 *
 * **Travaux** est volontairement plus libre que le reste : une liste de chantiers
 * avec priorité et coût estimé, sans échéance imposée. Elle se distingue
 * visuellement des écrans à dates fixes.
 *
 * **Administratif** porte l'inverse : des dates, mais dont certaines bougent
 * chaque année sans qu'aucune API ne les publie. On les affiche « à confirmer »
 * plutôt que de faire semblant de les connaître.
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';

import { type Tache, aujourdhuiParis, enClair, relatif } from '@foyer/noyau';

import { CarteTache, Chargement, Erreur, Section, TRAIT, Vide } from '../composants/ui.js';
import { useAppli, useRessource } from '../lib/appli.js';
import { donnees } from '../lib/donnees.js';
import { FeuilleTache } from './FeuilleTache.js';

const RANG_PRIORITE: Record<string, number> = { haute: 0, moyenne: 1, basse: 2 };

export function Travaux() {
  const { rafraichis } = useAppli();
  const aujourdhui = aujourdhuiParis();
  const taches = useRessource(() => donnees.taches({ categorie: 'travaux' }));
  const [feuille, setFeuille] = useState<Tache | 'nouvelle' | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);

  if (taches.chargement && !taches.donnee) return <Chargement quoi="Chargement des travaux" />;
  if (taches.erreur && !taches.donnee) return <Erreur message={taches.erreur} />;

  const liste = taches.donnee?.taches ?? [];
  const actives = liste
    .filter((t) => t.statut === 'active')
    .sort(
      (a, b) =>
        (RANG_PRIORITE[a.priorite ?? 'moyenne'] ?? 1) - (RANG_PRIORITE[b.priorite ?? 'moyenne'] ?? 1) ||
        (a.echeance ?? '9999').localeCompare(b.echeance ?? '9999'),
    );
  const faites = liste.filter((t) => t.statut === 'terminee');
  const budget = actives.reduce((somme, t) => somme + (t.coutEstime ?? 0), 0);

  async function coche(t: Tache) {
    setOccupe(t.id);
    try {
      await donnees.coche(t.id, t.titre);
      rafraichis();
    } finally {
      setOccupe(null);
    }
  }

  return (
    <>
      <div className="entete-vue">
        <h1>
          Travaux
        </h1>
        <button type="button" className="bouton" onClick={() => setFeuille('nouvelle')}>
          <Plus strokeWidth={TRAIT} size={18} aria-hidden />
          Ajouter
        </button>
      </div>
      <p className="chapeau">
        Les chantiers et réparations, par priorité. Pas d’échéance imposée : c’est une liste, pas un
        calendrier.
        {budget > 0 && ` Budget estimé : ${budget.toLocaleString('fr-FR')} €.`}
      </p>

      {actives.length === 0 && faites.length === 0 ? (
        <Vide titre="Aucun chantier." texte="Notez ici ce qui traîne : le joint du robinet, la peinture, le devis d’isolation." >
          <button type="button" className="bouton secondaire" onClick={() => setFeuille('nouvelle')}>
            Ajouter un chantier
          </button>
        </Vide>
      ) : (
        <>
          {actives.map((t) => (
            <div key={t.id}>
              <CarteTache
                tache={t}
                aujourdhui={aujourdhui}
                occupe={occupe === t.id}
                onCoche={() => void coche(t)}
                onOuvre={() => setFeuille(t)}
              />
              {(t.priorite || t.coutEstime !== null || t.piece) && (
                <div className="sourdine" style={{ margin: '-6px 0 12px 20px' }}>
                  {[
                    t.priorite ? `priorité ${t.priorite}` : null,
                    t.piece,
                    t.coutEstime !== null ? `≈ ${t.coutEstime.toLocaleString('fr-FR')} €` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              )}
            </div>
          ))}

          <Section titre="Terminés" compte={faites.length} ouverteParDefaut={false}>
            {faites.map((t) => (
              <CarteTache key={t.id} tache={t} aujourdhui={aujourdhui} onOuvre={() => setFeuille(t)} />
            ))}
          </Section>
        </>
      )}

      {feuille && (
        <FeuilleTache
          tache={feuille === 'nouvelle' ? undefined : feuille}
          categorieInitiale="travaux"
          onFerme={() => setFeuille(null)}
        />
      )}
    </>
  );
}

export function Administratif() {
  const { rafraichis } = useAppli();
  const aujourdhui = aujourdhuiParis();
  const taches = useRessource(() => donnees.taches({ categorie: 'administratif', statut: 'active' }));
  const catalogues = useRessource(() => donnees.catalogues());
  const [feuille, setFeuille] = useState<Tache | 'nouvelle' | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);

  if (taches.chargement && !taches.donnee) return <Chargement quoi="Chargement de l’échéancier" />;
  if (taches.erreur && !taches.donnee) return <Erreur message={taches.erreur} />;

  const liste = (taches.donnee?.taches ?? [])
    .filter((t) => t.echeance)
    .sort((a, b) => a.echeance!.localeCompare(b.echeance!));
  const aConfirmer = liste.filter((t) => t.echeanceAConfirmer);
  const resiliations = liste.filter((t) => t.resiliationLimite);

  async function coche(t: Tache) {
    setOccupe(t.id);
    try {
      await donnees.coche(t.id, t.titre);
      rafraichis();
    } finally {
      setOccupe(null);
    }
  }

  return (
    <>
      <div className="entete-vue">
        <h1>
          Administratif
        </h1>
        <button type="button" className="bouton" onClick={() => setFeuille('nouvelle')}>
          <Plus strokeWidth={TRAIT} size={18} aria-hidden />
          Ajouter
        </button>
      </div>

      {aConfirmer.length > 0 && (
        <div className="note">
          <strong>{aConfirmer.length} date(s) à confirmer.</strong> Les dates fiscales changent chaque
          année et aucune source ne les publie à l’avance : celles-ci portent une valeur par défaut, à
          vérifier une fois par an.
        </div>
      )}

      {resiliations.length > 0 && (
        <div className="note">
          Pour les assurances, le rappel tombe avant la fermeture de la fenêtre de résiliation, pas le
          jour de l’échéance — sinon il serait déjà trop tard.
        </div>
      )}

      {liste.length === 0 ? (
        <Vide
          titre="Rien d’enregistré."
          texte="Impôts, taxe foncière, assurances, pièces d’identité : ajoutez ce qui a une date."
        />
      ) : (
        liste.map((t) => (
          <div key={t.id}>
            <CarteTache
              tache={t}
              aujourdhui={aujourdhui}
              occupe={occupe === t.id}
              onCoche={() => void coche(t)}
              onOuvre={() => setFeuille(t)}
            />
            {t.resiliationLimite && (
              <div className="sourdine" style={{ margin: '-6px 0 12px 20px' }}>
                Résiliation possible jusqu’au {enClair(t.resiliationLimite)} —{' '}
                {relatif(t.resiliationLimite, aujourdhui).texte.toLowerCase()}
              </div>
            )}
          </div>
        ))
      )}

      {catalogues.donnee && catalogues.donnee.administratif.modeles.length > 0 && (
        <p className="sourdine">
          Gabarits disponibles : {catalogues.donnee.administratif.modeles.map((m) => m.titre).join(', ')}.
        </p>
      )}

      {feuille && (
        <FeuilleTache
          tache={feuille === 'nouvelle' ? undefined : feuille}
          categorieInitiale="administratif"
          onFerme={() => setFeuille(null)}
        />
      )}
    </>
  );
}
